import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { MatDialog } from '@angular/material/dialog';
import {
    ProjectDetailsService,
    SpacerQaqcTitleDto,
    SpacerQaqcTitleValueDto,
} from '../../services/project-details.service';
import {
    PickSpacerQaqcTitlesDialogComponent,
    PickSpacerQaqcTitlesDialogData,
} from './pick-spacer-qaqc-titles-dialog.component';

export interface ManageQaqcReadingsDialogData {
    projectId: number;
    projectStatus?: string;
}

export interface ManageSpacerQaqcTitleDto extends SpacerQaqcTitleDto {
    isNew?: boolean;
}

@Component({
    selector: 'app-manage-qaqc-readings',
    standalone: true,
    imports: [CommonModule, MaterialModule, FormsModule, SpinnerComponent],
    templateUrl: './manage-qaqc-readings.component.html',
    styleUrls: ['./manage-qaqc-readings.component.css'],
})
export class ManageQaqcReadingsComponent implements OnInit {
    isLoading = true;
    isSaving = false;
    titles: ManageSpacerQaqcTitleDto[] = [];
    removedTitles: SpacerQaqcTitleDto[] = [];
    predefinedTitles: SpacerQaqcTitleValueDto[] | null = null;
    projectStatus: string = '';

    constructor(
        public dialogRef: MatDialogRef<ManageQaqcReadingsComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ManageQaqcReadingsDialogData,
        private projectDetailsService: ProjectDetailsService,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.projectStatus = this.data.projectStatus ?? '';
        this.projectDetailsService.getProjectById(this.data.projectId).subscribe({
            next: (project) => {
                this.titles = (project.spacerQAQCTitles || []).map((t: any): ManageSpacerQaqcTitleDto => ({
                    id: t.id ?? 0,
                    cementProjId: t.cementProjId ?? this.data.projectId,
                    temperature: t.temperature ?? null,
                    fullTitle: t.fullTitle ?? '',
                    titleValue: {
                        id: t.titleValue?.id ?? 0,
                        title: t.titleValue?.title ?? '',
                        groupId: t.titleValue?.groupId ?? null,
                        groupName: t.titleValue?.groupName ?? null,
                    }
                }));
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            },
        });
    }

    addNew(): void {
        this.titles.push({
            id: 0,
            cementProjId: this.data.projectId,
            temperature: null,
            fullTitle: '',
            isNew: true,
            titleValue: {
                id: 0,
                title: '',
                groupId: null,
                groupName: null,
            }
        });
    }

    removeTitle(index: number): void {
        const target = this.titles[index];
        if (target && target.id > 0) {
            this.removedTitles.push({
                id: target.id,
                cementProjId: target.cementProjId,
                temperature: target.temperature,
                fullTitle: target.fullTitle,
                titleValue: {
                    id: target.titleValue.id,
                    title: target.titleValue.title,
                    groupId: target.titleValue.groupId,
                    groupName: target.titleValue.groupName,
                },
            });
        }

        this.titles.splice(index, 1);
    }

    pickFromExisting(): void {
        if (this.predefinedTitles) {
            this.openPickDialog(this.predefinedTitles);
            return;
        }

        this.projectDetailsService.getAllTitlesForSpacer().subscribe({
            next: (titles) => {
                this.predefinedTitles = titles ?? [];
                this.openPickDialog(this.predefinedTitles);
            },
            error: () => {
                // Keep behavior non-blocking if predefined titles cannot be loaded.
            },
        });
    }

    onSave(): void {
        this.isSaving = true;
        const normalizedTitles: SpacerQaqcTitleDto[] = this.titles.map((title) => ({
            id: title.id,
            cementProjId: this.data.projectId,
            temperature: title.temperature,
            fullTitle: this.buildFullTitle(title),
            titleValue: {
                id: title.titleValue.id,
                title: (title.titleValue.title ?? '').trim(),
                groupId: title.titleValue.groupId,
                groupName: title.titleValue.groupName,
            },
        }));

        this.projectDetailsService.updateProjectQaqcReadings(this.data.projectId, {
            readings: normalizedTitles,
            removeReadings: this.removedTitles,
        }).subscribe({
            next: (updatedTitles) => {
                this.isSaving = false;
                // Clear the isNew flag after successful save
                this.titles.forEach(t => t.isNew = false);
                this.dialogRef.close(updatedTitles);
            },
            error: () => {
                this.isSaving = false;
            },
        });
    }

    onClose(): void {
        this.dialogRef.close();
    }

    private openPickDialog(predefinedTitles: SpacerQaqcTitleValueDto[]): void {
        const alreadyAddedTitles = this.titles
            .filter((x) => x.titleValue?.id > 0)
            .map((x) => x.titleValue);

        const data: PickSpacerQaqcTitlesDialogData = {
            predefinedTitles,
            alreadyAddedTitles,
        };

        const dialogRef = this.dialog.open(PickSpacerQaqcTitlesDialogComponent, {
            data,
            width: '760px',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((selected: SpacerQaqcTitleValueDto[] | null | undefined) => {
            if (!selected || selected.length === 0) {
                return;
            }

            // Allow adding the same title multiple times - no duplicate checking
            for (const picked of selected) {
                this.titles.push({
                    id: 0,
                    cementProjId: this.data.projectId,
                    temperature: null,
                    fullTitle: picked.title,
                    titleValue: { ...picked },
                    isNew: false,
                });
            }
        });
    }

    private buildFullTitle(title: ManageSpacerQaqcTitleDto): string {
        const rawTitle = (title.titleValue?.title ?? '').trim();
        if (title.temperature === null || title.temperature === undefined || rawTitle.length === 0) {
            return rawTitle;
        }

        return `${rawTitle} @ ${title.temperature}°F`;
    }

    canRemoveTitle(title: ManageSpacerQaqcTitleDto): boolean {
        // If project is Draft, allow removing all titles
        if (this.projectStatus === 'Draft') {
            return true;
        }

        // For non-Draft projects, only allow removing newly added titles (not yet saved)
        return title.id === 0 || title.isNew === true;
    }
}
